import { useCallback, useEffect, useRef, useState } from 'react'

export type PermissiveNumberInputProps = {
	value: number
	onChange: (value: number) => void
	parse?: (value: number) => number // return NaN if invalid
}

// WIP -- test sandbox for vitest
export function usePermissiveNumberInput({
	value,
	onChange,
	parse = (value) => value,
}: PermissiveNumberInputProps) {
	const [internalValue, setInternalValue] = useState<string>(value.toString())
	const [isFocused, setIsFocused] = useState(false)
	const latestValueRef = useRef<number>(value)

	useEffect(() => {
		latestValueRef.current = value
	}, [value])

	// when internal value changes while focused, propagate as a number
	useEffect(() => {
		if (!isFocused) { return }
		// try turning it into a number
		const parsedFloat = parseFloat(internalValue ?? '')
		const parsed = parse(parsedFloat)

		// if NaN or matches latest external value, don't emit a change
		// This equality is the only thing stopping the loop, so `parse` must return
		// exactly what the caller stores. A caller that rounds or clamps inside its
		// own onChange never satisfies it and re-emits on every render.
		if (isNaN(parsed) || parsed === latestValueRef.current) {
			return
		}

		// otherwise, propagate as usual
		onChange(parsed)
	}, [internalValue, isFocused, parse, onChange])

	useEffect(() => {
		// update internval value if not focused
		if (!isFocused) {
			setInternalValue(value.toString())
		}
	}, [value, isFocused])

	const onFocus = useCallback(() => {
		setIsFocused(true)
	}, [])

	const onBlur = useCallback(() => {
		// revert to external value on blur
		setInternalValue(latestValueRef.current.toString())
		setIsFocused(false)
	}, [])

	return { internalValue, onInternalChange: setInternalValue, isFocused, onFocus, onBlur }
}
